
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import "./index.css";

import {
  Button,
  Card,
  Dropdown,
  Form,
  Input,
  message,
  notification,
  Typography,
} from "antd";

import { useTranslation } from "react-i18next";
import { useForm } from "antd/es/form/Form";

import {
  deleteUsersLinkedDevice,
  getUser,
  updateUserWithNotification,
} from "../../../Services/UserProfileServices";

import CommonLayout from "../CommonLayout";

import User_Profile_Header from "../../../Assets/user_profile_header.png";

import { UserRespose } from "../../../redux/Reducer/userReducer";
import { userInformation } from "../../../redux/actions";
import store from "../../../redux/store";

import {
  BellOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  LockOutlined,
  MailOutlined,
  MobileOutlined,
  MoreOutlined,
  UserOutlined,
} from "@ant-design/icons";

import { connect } from "react-redux";
import { translation } from "../../../i18n/translationConstant";

import { useFingerprintValidation } from "../../Authentication/FingerprintValidationContext";

import CommonModal from "../../Common/Components/CommonModal";
import { logError } from "../../../Utils/safeLog";

const UserProfile: React.FC<{
  setReduxUser: (user: any) => void;
}> = ({ setReduxUser }) => {
  const [user, setUser] = useState({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    vuaId: "",
    email: "",
    simBindingFlag: false,
    sendNotification: false,
  });

  const [changedValues, setChangedValues] = useState({
    firstName: "",
    lastName: "",
  });

  const [isProfileChanged, setIsProfileChanged] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [showVuaId, setShowVuaId] = useState(false);
  const [showMobileNumber, setShowMobileNumber] = useState(false);

  const { t } = useTranslation();
  const [form] = useForm();
  const { checkFingerprint } = useFingerprintValidation();

  const history = useHistory();

  const [mobileTokens, setMobileTokens] = useState<string[]>([]);

  const [isRemoveDeviceModalVisible, setIsRemoveDeviceModalVisible] = useState(false);

  // get user data 
  const getUserData = () => {
    getUser()
      .then((res) => {
        setUser(res.data);

        setMobileTokens(
          Array.isArray(res.data.mobileToken)
            ? res.data.mobileToken.filter(
                (token: string) => !!token
              )
            : []
        );
      })
      .catch((error) => {
        logError("Failed to fetch user profile", error);
      });
    }

  /*
   * Fetch user profile
   */
  useEffect(() => {
    
    getUserData();
    checkFingerprint();
  }, [checkFingerprint]);


  /*
   * Update form values when user data changes
   */
  useEffect(() => {
    setChangedValues({
      firstName: user.firstName,
      lastName: user.lastName,
    });

    form.setFieldsValue({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    });
  }, [user, form]);

  /*
   * Open remove device confirmation modal
   */
  const handleRemoveDevice = () => {
    setIsRemoveDeviceModalVisible(true);
  };

  /*
   * Remove linked mobile device
   */
  const confirmRemoveDevice = async () => {
    try {
      const response = await deleteUsersLinkedDevice();

      if (
        response.status === 200 &&
        response.data?.status === "SUCCESS"
      ) {
        setMobileTokens([]);
        setIsRemoveDeviceModalVisible(false);
        message.success(
          t("msgDeviceRemovedSuccessfully")
        );
        getUserData();
      } else {
        message.error(
          t("msgSomethingWentWrong")
        );
      }
    } catch (error) {
      logError("Failed to remove linked device", error);

      message.error(
        t("msgSomethingWentWrong")
      );
    }
  };

  /*
   * Update profile fields
   */
  const updateUserInfo = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsProfileChanged(true);

    if (event.target.id === "firstName") {
      setChangedValues((old) => ({
        ...old,
        firstName: event.target.value,
      }));
    } else {
      setChangedValues((old) => ({
        ...old,
        lastName: event.target.value,
      }));
    }
  };

  /*
   * Allow only alphabetic characters for names
   */
  const onNameChange = (e: any) => {
    e.target.value = e.target.value.replace(
      /[^A-Za-z]+/g,
      ""
    );
  };

  /*
   * Update user profile
   */
  const updateUserData = (values: any) => {
    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: user.email,
      // sendNotification: user.sendNotification,
    };

    updateUserWithNotification(payload).then(() => {
      notification.success({
        message: t("msgUserProfileSuccess"),
      });

      setReduxUser({
        firstName: changedValues.firstName,
        lastName: changedValues.lastName,
        mobileNumber: user.mobileNumber,
        vuaId: user.vuaId,
        email: user.email,
        sendNotification: user.sendNotification,
      });

      setIsProfileChanged(false);
    });
  };

  /*
   * Open Notification Preferences page
   */
  const handleNotificationPreferences = () => {
    history.push("/more/user-profile/notification-preferences");
  };

  const handleMobileIdentifier = () => {
    history.push("/more/mobile-numbers");
  };

  return (
    <CommonLayout
      title={t(translation.lblUserProfile)}
      showBackButton={true}
    >
      <div className="profileContent">

        {/* =====================================================
            Profile Intro
        ===================================================== */}
        <div className="profileIntro">
          <div>
            <Typography.Text className="profileIntroText">
              {t("lblKeepDetailsUpdated")}
            </Typography.Text>
          </div>

          <div className="profileSecureBadge">
            <LockOutlined />
            <span>
              {t("lblYourInformationIsSecure")}
            </span>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          name="userProfileForm"
          autoComplete="off"
          scrollToFirstError={true}
          onFinish={updateUserData}
          className="profileForm"
        >

          {/* =====================================================
              Identity
          ===================================================== */}
          <section
            className="profileSection identitySection"
            aria-labelledby="profileIdentityHeading"
          >
            <div className="identityBlock">

              <div className="avatarWrap">
                <img
                  className="userProfileImage"
                  src={User_Profile_Header}
                  alt={t("lblProfileIllustration")}
                />
              </div>

              <div className="identityText">
                <Typography.Title
                  level={4}
                  id="profileIdentityHeading"
                >
                  {`${user.firstName || ""} ${
                    user.lastName || ""
                  }`.trim() || t("lblYourProfile")}
                </Typography.Title>

                <Typography.Text>
                  {t(
                    "lblAccountDetailsNotificationPreferences"
                  )}
                </Typography.Text>
              </div>

            </div>
          </section>

          {/* =====================================================
              Personal Information
          ===================================================== */}
          <section
            className="profileSection"
            aria-labelledby="personalDetailsHeading"
          >
            <div className="sectionHeading">
              <div>
                <Typography.Title
                  level={4}
                  id="personalDetailsHeading"
                >
                  {t("lblPersonalInformation")}
                </Typography.Title>

                <Typography.Text>
                  {t(
                    "lblPersonalInformationDescription"
                  )}
                </Typography.Text>
              </div>
            </div>

            <div className="profileGrid">

              {/* First Name */}
              <Form.Item
                className="profileFormItem"
                name="firstName"
                label={t("lblFirstName")}
                rules={[
                  {
                    required: true,
                    message: t(
                      "errorRequiredFirstName"
                    ),
                  },
                ]}
              >
                <Input
                  id="firstName"
                  className="formInput"
                  value={changedValues.firstName}
                  prefix={<UserOutlined />}
                  onInput={onNameChange}
                  onChange={updateUserInfo}
                  aria-label={t("lblFirstName")}
                />
              </Form.Item>

              {/* Last Name */}
              <Form.Item
                className="profileFormItem"
                name="lastName"
                label={t("lblLastName")}
                rules={[
                  {
                    required: true,
                    message: t(
                      "errorRequiredLastName"
                    ),
                  },
                ]}
              >
                <Input
                  id="lastName"
                  className="formInput"
                  value={changedValues.lastName}
                  prefix={<UserOutlined />}
                  onInput={onNameChange}
                  onChange={updateUserInfo}
                  aria-label={t("lblLastName")}
                />
              </Form.Item>

              {/* Username / VUA ID */}
              <Form.Item
                className="profileFormItem"
                label={t("lblUserName")}
              >
                <Input
                  id="vuaid"
                  value={user.vuaId}
                  type={
                    showVuaId
                      ? "text"
                      : "password"
                  }
                  readOnly
                  className="formInput readonlyInput"
                  prefix={<LockOutlined />}
                  suffix={
                    <button
                      type="button"
                      className="visibilityButton"
                      onClick={() =>
                        setShowVuaId(!showVuaId)
                      }
                      aria-label={
                        showVuaId
                          ? t("lblHideUserName")
                          : t("lblShowUserName")
                      }
                    >
                      {showVuaId ? (
                        <EyeOutlined />
                      ) : (
                        <EyeInvisibleOutlined />
                      )}
                    </button>
                  }
                />

                <Typography.Text className="fieldHint">
                  {t(
                    "lblIdentifierCannotBeChanged"
                  )}
                </Typography.Text>
              </Form.Item>

              {/* Mobile Number */}
              <Form.Item
                className="profileFormItem"
                label={t("lblMobileNumber")}
              >
                <Input
                  id="mobileNumber"
                  value={user.mobileNumber}
                  type={
                    showMobileNumber
                      ? "text"
                      : "password"
                  }
                  readOnly
                  className="formInput readonlyInput"
                  prefix={<MobileOutlined />}
                  suffix={
                    <button
                      type="button"
                      className="visibilityButton"
                      onClick={() =>
                        setShowMobileNumber(
                          !showMobileNumber
                        )
                      }
                      aria-label={
                        showMobileNumber
                          ? t(
                              "lblHideMobileNumber"
                            )
                          : t(
                              "lblShowMobileNumber"
                            )
                      }
                    >
                      {showMobileNumber ? (
                        <EyeOutlined />
                      ) : (
                        <EyeInvisibleOutlined />
                      )}
                    </button>
                  }
                />

                <Typography.Text className="fieldHint">
                  {t(
                    "lblRegisteredMobileCannotBeChanged"
                  )}
                </Typography.Text>
              </Form.Item>

              {/* Email */}
              <Form.Item
                className="profileFormItem fullWidth"
                name="email"
                label={t("lblEmail")}
                rules={[
                  {
                    type: "email",
                    message: t(
                      "lblInvalidEmail"
                    ),
                  },
                  {
                    max: 100,
                    message: t(
                      "lblEmailTooLong"
                    ),
                  },
                ]}
              >
                <Input
                  id="email"
                  value={user.email}
                  placeholder={t("lblEnterEmail")}
                  prefix={<MailOutlined />}
                  className={`formInput ${
                    emailError
                      ? "emailErrorInput"
                      : ""
                  }`}
                  onChange={(e) => {
                    setIsProfileChanged(true);

                    const value =
                      e.target.value;

                    setUser((prev) => ({
                      ...prev,
                      email: value,
                    }));

                    form.setFieldValue(
                      "email",
                      value
                    );

                    if (value.trim()) {
                      setEmailError(false);
                    }
                  }}
                  aria-label={t("lblEmail")}
                />

                {emailError && (
                  <Typography.Text className="emailErrorText">
                    {t(
                      "lblAddEmailToEnableNotification"
                    )}
                  </Typography.Text>
                )}
              </Form.Item>

            </div>
          </section>

          {/* =====================================================
              Manage Mobile 
          ===================================================== */}
          {/* <section
            className="profileSection notificationSection"
            aria-labelledby="notificationHeading"
          >
            <div className="sectionHeading">
              <div>
                <Typography.Title
                  level={4}
                  id="notificationHeading"
                >
                  {t("lblMobileNumbersDescription")}
                </Typography.Title>
              </div>
            </div>
            
            <div
              className="notificationCard notificationPreferenceCard"
              onClick={
                handleMobileIdentifier
              }
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" ||
                  event.key === " "
                ) {
                  event.preventDefault();
                  handleMobileIdentifier();
                }
              }}
              aria-label={t(
                "lblPersonalizeNotifications"
              )}
            >
              <div className="notificationLeft">

                <div
                  className="notificationIcon"
                  aria-hidden="true"
                >
                  <BellOutlined />
                </div>

                <div className="notificationContent">

                  <Typography.Text className="summaryTitle">
                    {t(
                      "lblRegisteredMobileNumbers"
                    )}
                  </Typography.Text>

                  <Typography.Text className="summaryDesc">
                    {t(
                      "lblAddSecondaryMobileNumberHint"
                    )}
                  </Typography.Text>

                </div>
              </div>

              <span
                className="notificationArrow"
                aria-hidden="true"
              >
                →
              </span>
            </div>
          </section> */}

          {/* =====================================================
              Notifications
          ===================================================== */}
          <section
            className="profileSection notificationSection"
            aria-labelledby="notificationHeading"
          >
            <div className="sectionHeading">
              <div>
                <Typography.Title
                  level={4}
                  id="notificationHeading"
                >
                  {t("iblNotifications")}
                </Typography.Title>

                <Typography.Text>
                  {t("iblMonthlyUpdateRecive")}
                </Typography.Text>
              </div>
            </div>

            {/* Monthly Summary */}
            {/* <div className="notificationCard">
              <div className="notificationLeft">

                <div
                  className="notificationIcon"
                  aria-hidden="true"
                >
                  <MailOutlined />
                </div>

                <div className="notificationContent">

                  <Typography.Text className="summaryTitle">
                    {t("iblMonthlyUpdate")}
                  </Typography.Text>

                  <Typography.Text className="summaryDesc">
                    {t(
                      "iblMonthlyUpdateRecive"
                    )}
                  </Typography.Text>

                </div>
              </div>

              <Switch
                checked={user.sendNotification}
                onChange={(checked) => {
                  if (!user.email?.trim()) {
                    setEmailError(true);
                    return;
                  }

                  setEmailError(false);
                  setIsProfileChanged(true);

                  setUser((prev) => ({
                    ...prev,
                    sendNotification: checked,
                  }));
                }}
                aria-label={t(
                  "iblMonthlyUpdate"
                )}
              />
            </div> */}

            {/* Personalize Notifications */}
            <div
              className="notificationCard notificationPreferenceCard"
              onClick={
                handleNotificationPreferences
              }
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (
                  event.key === "Enter" ||
                  event.key === " "
                ) {
                  event.preventDefault();
                  handleNotificationPreferences();
                }
              }}
              aria-label={t(
                "lblPersonalizeNotifications"
              )}
            >
              <div className="notificationLeft">

                <div
                  className="notificationIcon"
                  aria-hidden="true"
                >
                  <BellOutlined />
                </div>

                <div className="notificationContent">

                  <Typography.Text className="summaryTitle">
                    {t(
                      "lblPersonalizeNotifications"
                    )}
                  </Typography.Text>

                  <Typography.Text className="summaryDesc">
                    {t(
                      "lblPersonalizeNotificationsDescription"
                    )}
                  </Typography.Text>

                </div>
              </div>

              <span
                className="notificationArrow"
                aria-hidden="true"
              >
                →
              </span>
            </div>
          </section>

          {/* =====================================================
              Linked Devices
          ===================================================== */}
          <section
            className="profileSection linkedDevicesSection"
            aria-labelledby="linkedDevicesHeading"
          >
            <div className="sectionHeading">
              <div>
                <h4 id="linkedDevicesHeading">
                  {t("lblLinkedDevices")}
                </h4>

                <span className="linkedDevicesDescription">
                  {t(
                    "lblDevicesCurrentlyLinked"
                  )}
                </span>
              </div>
            </div>

            {/* Device exists */}
            {user.simBindingFlag ? (
              <Card
                className="linkedDeviceCard"
                bordered={false}
              >
                <div className="linkedDeviceInfo">

                  <div className="linkedDeviceIcon">
                    <MobileOutlined />
                  </div>

                  <div className="linkedDeviceText">

                    <span className="linkedDeviceName">
                      {t("lblMobileDevice")}
                    </span>

                    <span className="linkedDeviceStatus">
                      <span className="linkedDeviceStatusDot" />
                      {t("lblLinked")}
                    </span>

                  </div>
                </div>

                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items: [
                      {
                        key: "remove",
                        danger: true,
                        icon: <DeleteOutlined />,
                        label: t("lblRemoveDevice"),
                        onClick: handleRemoveDevice,
                      },
                    ],
                  }}
                  placement="bottomRight"
                >
                  <button
                    type="button"
                    className="linkedDeviceMenuButton"
                    aria-label={t("lblDeviceOptions")}
                  >
                    <MoreOutlined />
                  </button>
                </Dropdown>
              </Card>
            ) : (
              <Card
                className="linkedDeviceCard noLinkedDeviceCard"
                bordered={false}
              >
                <div className="linkedDeviceInfo">

                  <div className="linkedDeviceIcon">
                    <MobileOutlined />
                  </div>

                  <div className="linkedDeviceText">

                    <span className="linkedDeviceName">
                      {t("lblNoDeviceLinked")}
                    </span>

                    <span className="linkedDeviceStatus noDeviceStatus">
                      {t("lblNoDeviceLinkedDescription")}
                    </span>

                  </div>
                </div>
              </Card>
            )}
          </section>

          {/* =====================================================
              Profile Actions
          ===================================================== */}
          <div className="profileActions">

            <Typography.Text className="saveHint">
              {isProfileChanged
                ? t("lblUnsavedChanges")
                : t("lblAllChangesSaved")}
            </Typography.Text>

            <Button
              htmlType="submit"
              className="profileSubmitButton"
              disabled={!isProfileChanged}
            >
              {t("lblUpdateDetails")}
            </Button>

          </div>
        </Form>

        {/* =====================================================
            Remove Device Modal
        ===================================================== */}
        <CommonModal
          visible={isRemoveDeviceModalVisible}
          onCancel={() =>
            setIsRemoveDeviceModalVisible(false)
          }
        >
          <div className="logoutModalContent">

            <Typography.Text className="logoutModalTitle">
              {t("lblRemoveLinkedDevice")}
            </Typography.Text>

            <Typography.Text className="logoutModalMessage">
              {t(
                "lblConfirmRemoveLinkedDevice"
              )}
            </Typography.Text>

            <div className="logoutModalActions">

              <Button
                className="logoutCancelButton"
                onClick={() =>
                  setIsRemoveDeviceModalVisible(
                    false
                  )
                }
              >
                {t(translation.lblCancel)}
              </Button>

              <Button
                className="logoutConfirmButton"
                onClick={confirmRemoveDevice}
              >
                {t("lblRemoveDevice")}
              </Button>

            </div>
          </div>
        </CommonModal>
      </div>
    </CommonLayout>
  );
};

const mapDispatchToProps = () => {
  return {
    setReduxUser: (changedUser: UserRespose) =>
      store.dispatch(
        userInformation(changedUser)
      ),
  };
};

export default connect(
  null,
  mapDispatchToProps
)(UserProfile);